import { Box, FileText, LayoutGrid, Plus, Repeat, Trash2, Type } from 'lucide-react';
import type { ContainerNode } from '@news-cms/shared';
import { useEditor } from './editor-context';
import { NodeToolbar } from './node-toolbar';
import { ContainerChildrenRenderer } from './node-renderer';

export function ContainerNodeEditor({
  node,
  isRoot = false,
}: {
  node: ContainerNode;
  isRoot?: boolean;
}) {
  const {
    selectedId,
    select,
    updateContainer,
    addContainerToParent,
    addRowToParent,
    addSlotToParent,
    addCarouselToParent,
    addLabelToParent,
    remove,
  } = useEditor();
  const isSelected = selectedId === node.id;

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        select(node.id);
      }}
      className={`relative rounded-md border border-dashed px-px pb-0.5 pt-3 transition ${
        isSelected
          ? 'border-blue-400 bg-blue-50/40 dark:border-blue-500 dark:bg-blue-950/20'
          : 'border-slate-300 dark:border-slate-700'
      }`}
    >
      <div className="mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-400">
        <Box className="h-3 w-3" />
        Container · {node.variant}
        {isRoot && (
          <span className="rounded bg-slate-200 px-1.5 text-[9px] dark:bg-slate-700">root</span>
        )}
      </div>

      <NodeToolbar
        visible={isSelected}
        buttons={[
          {
            key: 'row',
            label: 'Row',
            icon: <LayoutGrid className="h-3.5 w-3.5" />,
            onClick: () => addRowToParent(node.id),
          },
          {
            key: 'container',
            label: 'Container',
            icon: <Box className="h-3.5 w-3.5" />,
            onClick: () => addContainerToParent(node.id),
          },
          {
            key: 'slot',
            label: 'Slot',
            icon: <FileText className="h-3.5 w-3.5" />,
            onClick: () => addSlotToParent(node.id),
          },
          {
            key: 'carousel',
            label: 'Carousel',
            icon: <Repeat className="h-3.5 w-3.5" />,
            onClick: () => addCarouselToParent(node.id),
          },
          {
            key: 'label',
            label: 'Label',
            icon: <Type className="h-3.5 w-3.5" />,
            onClick: () => addLabelToParent(node.id),
          },
          {
            key: 'variant',
            label: node.variant === 'fluid' ? '→ fixed' : '→ fluid',
            icon: <Repeat className="h-3.5 w-3.5" />,
            onClick: () =>
              updateContainer(node.id, { variant: node.variant === 'fluid' ? 'fixed' : 'fluid' }),
          },
          {
            key: 'delete',
            label: 'ลบ',
            icon: <Trash2 className="h-3.5 w-3.5" />,
            onClick: () => remove(node.id),
            variant: 'destructive',
            disabled: isRoot,
          },
        ]}
      />

      <ContainerChildrenRenderer children={node.children} />

      {/* Empty-state inline add button (in addition to toolbar) */}
      {node.children.length === 0 && (
        <div className="mt-2 flex justify-center gap-1.5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              addRowToParent(node.id);
            }}
            className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <Plus className="h-3 w-3" /> Row
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              addSlotToParent(node.id);
            }}
            className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <Plus className="h-3 w-3" /> Slot
          </button>
        </div>
      )}
    </div>
  );
}
